import { Flex } from '@aws-amplify/ui-react';
import type { QueryCondition } from '@edulead/models';
import type { Edulead } from 'edulead';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import CustomList from '../custom-components/CustomList';
import LoadingBars from '../custom-components/LoadingBars';
import NothingHere from '../custom-components/NothingHere';
import { GlobalContext } from '../global-context';
import { toastV4Error } from '../services/edulead-v4';
import { eduleadV4 } from '../services/edulead-v4-client';
import { Entity, SortOptions } from '../utils/CustomTypes';
import {
  legacyGroupedPeopleResponse,
  LegacyGroupedPeopleResponse,
  legacyPeopleResponse,
  LegacyPeopleResponse,
  personOrder,
  toLegacyPerson,
} from '../utils/legacyPerson';
import { LocalStorageKeys, LocalStorageService } from '../utils/LocalStorageService';
import { handleNewExport } from '../utils/newExportFeature';
import { TokenPagination } from './TokenPagination';

const PAGE_SIZE = 25;
/** By account: the people shown of an account before Load more, and how many Load more adds. */
const PEOPLE_PER_ACCOUNT = 10;
const MORE_PEOPLE_PER_ACCOUNT = 100;

export interface PeopleResultsProps {
  /** The conditions the API runs, as the search sends them (the export sends the same). */
  conditions: QueryCondition[];
  /**
   * What identifies the search apart from its page and sort: a change empties the selection
   * and shows the full loading state, where a page or sort change shows an overlay.
   */
  searchKey: string;
  /** The console page the search ran on, for the search history; none records nothing. */
  source?: Edulead.SearchPeopleRequestSource;
  /** The saved search the export comes from, when known. */
  savedSearchId?: string;
  /** Told as a load starts and ends (a page or sort change too), for a button's spinner. */
  onLoadingChange?: (isLoading: boolean) => void;
  /** Whether the list's toolbar and column header stay at the top while it scrolls. */
  stickyHeader?: boolean;
  /**
   * By account: a page is PAGE_SIZE accounts, each with its first people and a Load more, as
   * the Buying Groups page lists them. Absent: a flat page of people.
   */
  groupBy?: 'account';
}

/**
 * The people a search returns, paged: the list with its sort, selection and export, and the
 * pager; flat, or by account. The page (`page`, `pageToken`) and sort (`sortBy`, `sortDir`) are
 * the URL's, so a refresh or a shared link keeps its place; the other query params are left as
 * they are. The People directory and the Navigator's People view both show this.
 */
export function PeopleResults({
  conditions,
  searchKey,
  source,
  savedSearchId,
  onLoadingChange,
  stickyHeader,
  groupBy,
}: PeopleResultsProps) {
  const globalContext = useContext(GlobalContext);
  const location = useLocation();
  const navigate = useNavigate();

  const params = new URLSearchParams(location.search);
  const page = parseInt(params.get('page') ?? '') || 1;
  const pageToken = params.get('pageToken') || undefined;
  const sortBy = params.get('sortBy') || 'relevance';
  const sortDir: 'asc' | 'desc' = params.get('sortDir') === 'asc' ? 'asc' : 'desc';
  // Grouping changes what a page is, so it is part of what identifies the search.
  const groupedKey = `${searchKey}|${groupBy ?? ''}`;
  const sortKey = `${groupedKey}|${sortBy}|${sortDir}`;

  const [isLoading, setIsLoading] = useState(false);
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const [response, setResponse] = useState<LegacyPeopleResponse | null>(null);
  const [grouped, setGrouped] = useState<LegacyGroupedPeopleResponse | null>(null);
  const [loadingMoreAccountIds, setLoadingMoreAccountIds] = useState<Set<string>>(new Set());
  const [nextPageToken, setNextPageToken] = useState('');
  /** The tokens of the pages seen of the current search (tokens[i] loads page i + 1), for Previous. */
  const pageTokens = useRef<{ search: string; tokens: string[] }>({ search: '', tokens: [''] });
  const [isExpanded, setIsExpanded] = useState(
    LocalStorageService.get<boolean>(LocalStorageKeys.IS_CONTACTS_LIST_ON_CONTACTS_PAGE_EXPANDED),
  );
  const [selectedRows, setSelectedRows] = useState<{ [key: string]: any }>({});
  /** The search the shown rows are of; another search loads in full, a page or sort as an overlay. */
  const shownSearch = useRef<string | null>(null);

  useEffect(() => {
    const isNewSearch = shownSearch.current !== groupedKey;
    shownSearch.current = groupedKey;
    if (isNewSearch) setSelectedRows({});
    const hasRows = groupBy ? !!grouped?.data?.data?.length : !!response?.data?.data?.length;
    load(isNewSearch || !hasRows);
  }, [location.search, groupedKey]);

  async function load(inFull: boolean) {
    try {
      if (inFull) setIsLoading(true);
      else setIsPaginationLoading(true);
      onLoadingChange?.(true);
      if (pageTokens.current.search !== sortKey) {
        pageTokens.current = { search: sortKey, tokens: [''] };
      }
      if (pageToken) {
        pageTokens.current.tokens[page - 1] = pageToken;
      }
      const result = await eduleadV4.people.search({
        conditions: JSON.stringify(conditions),
        source,
        order_by: personOrder(sortBy, sortDir),
        page_size: PAGE_SIZE,
        page_token: pageToken,
        ...(groupBy ? { group_by: groupBy, people_per_group: PEOPLE_PER_ACCOUNT } : {}),
      });
      const next = result.response.next_page_token ?? '';
      if (next) {
        pageTokens.current.tokens[page] = next;
      }
      setNextPageToken(next);
      if (groupBy) setGrouped(legacyGroupedPeopleResponse(result.response));
      else setResponse(legacyPeopleResponse(result.data, result.response));
    } catch (err) {
      toastV4Error(err, 'Something went wrong loading contacts');
    } finally {
      setIsLoading(false);
      setIsPaginationLoading(false);
      onLoadingChange?.(false);
    }
  }

  /** By account: the account's next people, after the ones shown, onto its group. */
  const loadMoreContacts = useCallback(
    async (accountId: string) => {
      try {
        setLoadingMoreAccountIds((prev) => new Set(prev).add(accountId));
        const result = await eduleadV4.people.search({
          conditions: JSON.stringify([
            ...conditions,
            { field: 'accountId', operator: 'equals', value: accountId },
          ]),
          order_by: personOrder(sortBy, sortDir),
          page_size: MORE_PEOPLE_PER_ACCOUNT,
        });
        const loaded = result.data.map(toLegacyPerson);
        setGrouped((prev) => {
          if (!prev?.data?.data) return prev;
          return {
            ...prev,
            data: {
              ...prev.data,
              data: prev.data.data.map((account) => {
                if (account.accountId !== accountId) return account;
                const shown = new Set(account.contacts.map((c) => c.id));
                const more = loaded.filter((c) => !shown.has(c.id));
                return { ...account, contacts: [...account.contacts, ...more] };
              }),
            },
          };
        });
      } catch (err) {
        toastV4Error(err, 'Something went wrong loading more contacts');
      } finally {
        setLoadingMoreAccountIds((prev) => {
          const next = new Set(prev);
          next.delete(accountId);
          return next;
        });
      }
    },
    [conditions, sortBy, sortDir],
  );

  /** Goes to a page of the search, keeping the URL's other params. */
  const goTo = (nextPage: number, token: string, sort?: SortOptions) => {
    const next = new URLSearchParams(location.search);
    next.set('page', String(nextPage));
    if (token) next.set('pageToken', token);
    else next.delete('pageToken');
    if (sort) {
      next.set('sortBy', sort.sortBy ?? '');
      next.set('sortDir', sort.sortDir ?? '');
    }
    navigate(`${location.pathname}?${next.toString()}`);
  };

  const onSort = (sort: SortOptions) => goTo(1, '', sort);

  // Next and Previous keep the page's token in the URL; a token not seen in this visit (a link to
  // a later page) sends Previous to page 1.
  const onPrevious = () => {
    const previous = page - 1;
    if (previous < 1) return;
    const token = previous === 1 ? '' : pageTokens.current.tokens[previous - 1];
    if (token === undefined) goTo(1, '');
    else goTo(previous, token);
  };

  const onNewExport = useCallback(
    async (config: { type: 'selected' | 'all'; ids?: string[] }) => {
      // For selected items, use ids.length; for all, use total from search response
      const total = groupBy ? grouped?.data?.totalContacts : response?.data?.total;
      const estimatedCount =
        config.type === 'selected' && config.ids?.length ? config.ids.length : total || 0;
      const exportId = await handleNewExport({
        ...config,
        entity: 'CONTACT',
        searchParams: { pageSize: PAGE_SIZE, entity: 'contacts' },
        query: conditions,
        estimatedCount,
        savedSearchId,
      });
      navigate(`/exports/${exportId}/target`);
    },
    [conditions, grouped, groupBy, navigate, response, savedSearchId],
  );

  if (isLoading) {
    return (
      <div className="content content-1000">
        <LoadingBars numberOfRows={6} />
      </div>
    );
  }
  const tableFields = globalContext.environment.entities.contact.tableFields;

  if (groupBy) {
    if (!grouped?.data?.data?.length) {
      return (
        <div className="content content-1000">
          <NothingHere text="No contacts found..." />
        </div>
      );
    }
    return (
      <div className="content content-1000">
        <CustomList
          searchResponse={grouped}
          isLoading={isLoading}
          isPaginationLoading={isPaginationLoading}
          isExpanded={false}
          isExporting={false}
          onSort={onSort}
          onExport={() => {}}
          onNewExport={onNewExport}
          onExpandToggle={() => {}}
          stickyHeader={stickyHeader}
          showExpandButton={false}
          showSubheader={false}
          showRowActionButtons={false}
          showMainInstitution={false}
          showColumnVisibilityButton={false}
          currentSort={{ sortBy, sortDir }}
          tableFields={tableFields}
          entity={Entity.CONTACT}
          selectedRows={selectedRows}
          onSelectionChange={setSelectedRows}
          groupedData={grouped.data.data}
          groupedTotalContacts={grouped.data.totalContacts}
          groupedTotalAccounts={grouped.data.total}
          onLoadMoreContacts={loadMoreContacts}
          loadingMoreAccountIds={loadingMoreAccountIds}
        />
        <Flex marginTop={30} marginBottom={50} justifyContent="center">
          <TokenPagination
            page={page}
            pageSize={PAGE_SIZE}
            shown={grouped.data.data.length}
            total={grouped.data.total}
            hasNext={nextPageToken !== ''}
            isLoading={isPaginationLoading}
            onPrevious={onPrevious}
            onNext={() => goTo(page + 1, nextPageToken)}
          />
        </Flex>
      </div>
    );
  }

  if (!response?.data?.data?.length) {
    return (
      <div className="content content-1000">
        <NothingHere text="No contacts found..." />
      </div>
    );
  }

  return (
    <div className={`content ${isExpanded ? 'content-1600' : 'content-1000'}`}>
      <CustomList
        searchResponse={response}
        isLoading={isLoading}
        isPaginationLoading={isPaginationLoading}
        isExporting={false}
        onSort={onSort}
        onExport={() => {}}
        onNewExport={onNewExport}
        isExpanded={isExpanded}
        stickyHeader={stickyHeader}
        onExpandToggle={() => {
          const next = !isExpanded;
          setIsExpanded(next);
          LocalStorageService.set(
            LocalStorageKeys.IS_CONTACTS_LIST_ON_CONTACTS_PAGE_EXPANDED,
            next,
          );
        }}
        currentSort={{ sortBy, sortDir }}
        showExpandButton={true}
        entity={Entity.CONTACT}
        tableFields={tableFields}
        onEvent={() => {}}
        selectedRows={selectedRows}
        onSelectionChange={setSelectedRows}
      />
      <Flex marginTop={30} marginBottom={50} justifyContent="center">
        <TokenPagination
          page={page}
          pageSize={PAGE_SIZE}
          shown={response.data.data.length}
          total={response.data.total}
          hasNext={nextPageToken !== ''}
          isLoading={isPaginationLoading}
          onPrevious={onPrevious}
          onNext={() => goTo(page + 1, nextPageToken)}
        />
      </Flex>
    </div>
  );
}
